/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useMemo } from 'react'
// @mui
import Card from '@mui/material/Card'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Unstable_Grid2'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

// routes
import { paths } from 'src/routes/paths'
import { useParams, useRouter } from 'src/routes/hook'

// components
import Iconify from 'src/components/iconify'
import { useSettingsContext } from 'src/components/settings'
import Datagrid from 'src/components/datagrid'
import DateTimeRangePicker from 'src/components/date-time-range-picker'
import { Helmet } from 'react-helmet-async'
import axiosInstance from 'src/utils/axios'
import { useSnackbar } from 'notistack'
import { useBoolean } from 'src/hooks/use-boolean'
import { masks } from 'src/utils/masks'
import { fNumber } from 'src/utils/format-number'
import useTransaction, { useCardBalance, useCardInfo } from 'src/service/transaction.service'
import Chart, { useChart } from 'src/components/chart'
import UserTableRow from './table-user/user-table-row'
import UserCreateEditForm from './user-create-edit-view'
import CardEditForm from '../user-create-edit-view'

const TABLE_HEAD = [
  { id: 'card', label: 'Cartão' },
  { id: 'type', label: 'Tipo' },
  { id: 'value', label: 'Valor', width: 160 },
  { id: 'createdAt', label: 'Data e hora' },
  { id: 'event', label: 'Evento' },
  { id: 'order', label: 'Compra' },
  { id: '', width: 60 },
]

const defaultPagination = {
  page: 1,
  perPage: 8,
  sort: { createdAt: 'desc' },
}

export default function TransactionListById() {
  const [pagination, setPagination] = useState(defaultPagination)
  const [type, setType] = useState<'all' | 'entry' | 'exit'>('all')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const params = useParams()
  const router = useRouter()
  const cardId = params.id!

  const queryFilters = useMemo(
    () => ({
      type: type !== 'all' ? type : undefined,
      dateStart: startDate ? startDate.toISOString() : undefined,
      dateEnd: endDate ? endDate.toISOString() : undefined,
    }),
    [type, startDate, endDate],
  )

  const { isLoading, isFetching, data, refetch } = useTransaction(pagination, cardId, queryFilters)
  const { data: balance, refetch: refetchBalance } = useCardBalance(cardId, {
    dateStart: queryFilters.dateStart,
    dateEnd: queryFilters.dateEnd,
  })
  const { data: cardInfo, refetch: refetchCardInfo } = useCardInfo(cardId)

  const settings = useSettingsContext()
  const { enqueueSnackbar } = useSnackbar()
  const quickEdit = useBoolean()
  const editName = useBoolean()

  const notFound = !data?.data.length

  const refreshAll = useCallback(() => {
    refetch()
    refetchBalance()
  }, [refetch, refetchBalance])

  const handleDeleteRow = useCallback(
    async (id: string) => {
      try {
        await axiosInstance.delete(`/transaction/${id}`)
        enqueueSnackbar('Transação removida com sucesso !')
        refreshAll()
      } catch (error) {
        if (error?.message) {
          enqueueSnackbar(error?.message, { variant: 'error' })
        } else {
          enqueueSnackbar('Erro ao remover transação !', { variant: 'error' })
        }
      }
    },
    [enqueueSnackbar, refreshAll],
  )

  const handleDateChange = useCallback((s: Date | null, e: Date | null) => {
    setStartDate(s)
    setEndDate(e)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }, [])

  const handleTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setType(e.target.value as any)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }, [])

  const chartOptions = useChart({
    labels: ['Recargas (entradas)', 'Gastos (saídas)'],
    colors: ['#10b981', '#f59e0b'],
    legend: { position: 'bottom', fontSize: '12px', fontWeight: 600 },
    stroke: { width: 2, colors: ['#fff'] },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (val: any) => masks.currencyAllPlatforms(val) } },
    plotOptions: {
      pie: {
        donut: {
          size: '72%',
          labels: {
            show: true,
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 700,
              formatter: (val: any) => masks.currencyAllPlatforms(Number(val)),
            },
            total: {
              show: true,
              label: 'Saldo',
              fontSize: '12px',
              formatter: () => masks.currencyAllPlatforms(balance?.balance || 0),
            },
          },
        },
      },
    },
  })

  const hasMovements = (balance?.totalEntry || 0) + (balance?.totalExit || 0) > 0
  const cardTitle = cardInfo?.name || cardInfo?.card || 'Cartão'

  return (
    <>
      <Helmet>
        <title>Cashless - Movimentações</title>
      </Helmet>

      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            mb: { xs: 2, md: 2.5 },
            color: 'white',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent='space-between'
            spacing={1.5}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <IconButton
                onClick={() => router.push(paths.dashboard.transaction.root)}
                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}
                size='small'
              >
                <Iconify icon='eva:arrow-ios-back-fill' width={18} />
              </IconButton>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255,255,255,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon='solar:card-bold' width={22} />
              </Box>
              <Box>
                <Stack direction='row' alignItems='center' spacing={0.75}>
                  <Typography variant='subtitle1' fontWeight={700} sx={{ fontSize: { xs: 16, sm: 18 } }}>
                    {cardTitle}
                  </Typography>
                  <Tooltip title='Editar nome' arrow>
                    <IconButton
                      onClick={editName.onTrue}
                      size='small'
                      sx={{ color: 'rgba(255,255,255,0.85)' }}
                    >
                      <Iconify icon='solar:pen-bold' width={15} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant='caption' sx={{ opacity: 0.9, fontSize: 12 }}>
                  {cardInfo?.name ? `Cartão ${cardInfo?.card} · ` : ''}
                  {cardInfo?.event?.name || 'Sem evento associado'}
                </Typography>
              </Box>
            </Stack>

            <Button
              onClick={() => quickEdit.onTrue()}
              variant='contained'
              color='inherit'
              size='small'
              startIcon={<Iconify icon='mingcute:add-line' />}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              Ajustar saldo
            </Button>
          </Stack>
        </Box>

        {/* KPIs */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <SummaryCard
            icon='solar:wallet-money-bold'
            color={balance && balance.balance < 0 ? '#ef4444' : '#6366f1'}
            label='Saldo disponível'
            value={masks.currencyAllPlatforms(balance?.balance || 0)}
            sub='Entradas menos saídas'
          />
          <SummaryCard
            icon='solar:arrow-down-bold'
            color='#10b981'
            label='Total em recargas'
            value={masks.currencyAllPlatforms(balance?.totalEntry || 0)}
            sub={`${fNumber(balance?.entryCount || 0)} entradas`}
          />
          <SummaryCard
            icon='solar:cart-large-2-bold'
            color='#f59e0b'
            label='Total gasto'
            value={masks.currencyAllPlatforms(balance?.totalExit || 0)}
            sub={`${fNumber(balance?.exitCount || 0)} saídas`}
          />
          <SummaryCard
            icon='solar:bill-list-bold'
            color='#8b5cf6'
            label='Movimentações'
            value={fNumber(balance?.totalTransactions || 0)}
            sub='No período selecionado'
          />
        </Grid>

        {/* Filtros + gráfico */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid xs={12} md={7}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.neutral',
                }}
              >
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Iconify icon='solar:filter-bold' width={16} sx={{ color: 'primary.main' }} />
                  <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
                    Filtros
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={1.5}>
                  <Grid xs={12} sm={8}>
                    <DateTimeRangePicker
                      label='Período (data e hora)'
                      size='small'
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateChange}
                    />
                  </Grid>
                  <Grid xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label='Tipo'
                      value={type}
                      onChange={handleTypeChange}
                    >
                      <MenuItem value='all'>Todas</MenuItem>
                      <MenuItem value='entry'>Entradas (recargas)</MenuItem>
                      <MenuItem value='exit'>Saídas (gastos)</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>

          <Grid xs={12} md={5}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.neutral',
                }}
              >
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Iconify icon='solar:pie-chart-3-bold' width={16} sx={{ color: 'primary.main' }} />
                  <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
                    Recargas x Gastos
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ p: 2 }}>
                {hasMovements ? (
                  <Chart
                    type='donut'
                    series={[balance?.totalEntry || 0, balance?.totalExit || 0]}
                    options={chartOptions}
                    height={240}
                  />
                ) : (
                  <Stack alignItems='center' justifyContent='center' sx={{ height: 240 }}>
                    <Iconify
                      icon='solar:pie-chart-3-bold-duotone'
                      width={48}
                      sx={{ color: 'text.disabled', mb: 1 }}
                    />
                    <Typography variant='body2' color='text.secondary'>
                      Sem movimentações no período
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Tabela de movimentações */}
        <Card>
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
              Extrato de movimentações
            </Typography>
          </Box>
          <Datagrid
            data={data?.data || []}
            notFound={notFound}
            isLoading={isLoading || isFetching}
            headLabel={TABLE_HEAD}
            refetch={refetch}
            total={data?.total || 0}
            setPagination={setPagination}
            pagination={pagination}
            noSelection
            handleDeleteRows={() => {}}
            ItemContent={UserTableRow}
            handleDeleteRow={handleDeleteRow}
            handleEditRow={() => {}}
          />
        </Card>
      </Container>

      <UserCreateEditForm
        currentUser={{ card: cardId }}
        onRefleshTable={refreshAll}
        open={quickEdit.value}
        onClose={quickEdit.onFalse}
      />

      <CardEditForm
        currentUser={cardInfo}
        onRefleshTable={() => refetchCardInfo()}
        open={editName.value}
        onClose={editName.onFalse}
      />
    </>
  )
}

// ----------------------------------------------------------------------

function SummaryCard({
  icon,
  color,
  label,
  value,
  sub,
}: {
  icon: string
  color: string
  label: string
  value: string
  sub?: string
}) {
  return (
    <Grid xs={6} md={3}>
      <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <Stack direction='row' alignItems='center' spacing={1.25} sx={{ mb: 0.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${color}1a`,
              color,
              flexShrink: 0,
            }}
          >
            <Iconify icon={icon} width={17} />
          </Box>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            {label}
          </Typography>
        </Stack>
        <Typography variant='h6' fontWeight={700} sx={{ fontSize: 18, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {sub ? (
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
            {sub}
          </Typography>
        ) : null}
      </Paper>
    </Grid>
  )
}
